import { randomUuid } from '../utils/randomUuid';
import { sha256Hex } from '../utils/sha256';

export const BROWSER_HOST_INPUT_MAX_BYTES = 100 * 1024 * 1024;
export const BROWSER_HOST_INPUT_CHUNK_BYTES = 4 * 1024 * 1024;

type JsonRecord = Record<string, unknown>;
type InputRequest = (
  path: string,
  body: JsonRecord,
  signal: AbortSignal,
) => Promise<unknown>;

export type BrowserHostInputResult = {
  inputRef: `input:v1:${string}`;
  fileName: string;
  byteLength: number;
  mediaType: string;
  checksum: { algorithm: 'sha256'; digest: string };
};

export type BrowserHostInputUploadOptions = {
  file: File;
  sessionId: string;
  turnId: string;
  uploadId?: string;
  signal?: AbortSignal;
  request?: InputRequest;
  maxRecoveryAttempts?: number;
  sleep?: (milliseconds: number) => Promise<void>;
};

type TransferIdentity = {
  sessionId: string;
  turnId: string;
  uploadId: string;
  inputRef: `input:v1:${string}`;
  uploadRef: `upload:v1:${string}`;
};

/** Uploads one browser File without exposing a Host path or buffering it as one base64 body. */
export async function uploadBrowserHostInput(
  options: BrowserHostInputUploadOptions,
): Promise<BrowserHostInputResult> {
  validateUploadOptions(options);
  const signal = options.signal ?? new AbortController().signal;
  const request = options.request ?? browserInputRequest;
  const sleep = options.sleep ?? (milliseconds => new Promise(resolve => setTimeout(resolve, milliseconds)));
  const maxRecoveryAttempts = options.maxRecoveryAttempts ?? 3;
  if (!Number.isSafeInteger(maxRecoveryAttempts) || maxRecoveryAttempts < 1 || maxRecoveryAttempts > 10) {
    throw new Error('Browser Host input recovery policy is invalid.');
  }
  signal.throwIfAborted();
  const bytes = await options.file.arrayBuffer();
  signal.throwIfAborted();
  const digest = await sha256Hex(bytes);
  const uploadId = options.uploadId ?? `web-${randomUuid()}`;
  validateId('uploadId', uploadId);
  const declaration = {
    sessionId: options.sessionId,
    turnId: options.turnId,
    uploadId,
    fileName: options.file.name,
    mediaType: options.file.type,
    declaredSize: options.file.size,
    checksum: { algorithm: 'sha256', digest },
  };
  const begun = await requestWithRetry(
    request, '/inputs/begin', declaration, signal, maxRecoveryAttempts, sleep,
  );
  const begin = exactRecord(begun, ['inputRef', 'uploadRef', 'nextOffset']);
  if (!validRef(begin.inputRef, 'input') || !validRef(begin.uploadRef, 'upload') || begin.nextOffset !== 0) {
    throw new Error('Browser Host input begin response is invalid.');
  }
  const identity: TransferIdentity = {
    sessionId: options.sessionId,
    turnId: options.turnId,
    uploadId,
    inputRef: begin.inputRef,
    uploadRef: begin.uploadRef,
  };

  let offset = 0;
  while (offset < options.file.size) {
    signal.throwIfAborted();
    const end = Math.min(offset + BROWSER_HOST_INPUT_CHUNK_BYTES, options.file.size);
    const chunk = new Uint8Array(await options.file.slice(offset, end).arrayBuffer());
    signal.throwIfAborted();
    try {
      const response = exactRecord(await request('/inputs/chunk', {
        ...identity,
        offset,
        dataBase64: base64(chunk),
      }, signal), ['nextOffset']);
      if (response.nextOffset !== end) throw new Error('Browser Host input chunk response is invalid.');
      offset = end;
    } catch (error) {
      if (!isRetryable(error)) throw error;
      const resumed = exactRecord(await requestWithRetry(
        request, '/inputs/resume', identity, signal, maxRecoveryAttempts, sleep,
      ), ['nextOffset']);
      if (!Number.isSafeInteger(resumed.nextOffset) ||
        (resumed.nextOffset !== offset && resumed.nextOffset !== end)) {
        throw new Error('Browser Host input resume offset is invalid.');
      }
      offset = resumed.nextOffset as number;
    }
  }

  const committed = exactRecord(
    await requestWithRetry(
      request, '/inputs/commit', identity, signal, maxRecoveryAttempts, sleep,
    ),
    ['inputRef', 'byteLength', 'mediaType', 'checksum'],
  );
  if (committed.inputRef !== identity.inputRef || committed.byteLength !== options.file.size ||
    !validMediaType(committed.mediaType) || !validChecksum(committed.checksum) ||
    committed.checksum.digest !== digest) {
    throw new Error('Browser Host input commit response is invalid.');
  }
  return {
    inputRef: identity.inputRef,
    fileName: options.file.name,
    byteLength: committed.byteLength as number,
    mediaType: committed.mediaType as string,
    checksum: structuredClone(committed.checksum) as BrowserHostInputResult['checksum'],
  };
}

async function requestWithRetry(
  request: InputRequest,
  path: string,
  body: JsonRecord,
  signal: AbortSignal,
  attempts: number,
  sleep: (milliseconds: number) => Promise<void>,
): Promise<unknown> {
  let lastError: unknown;
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    signal.throwIfAborted();
    try {
      return await request(path, body, signal);
    } catch (error) {
      if (!isRetryable(error)) throw error;
      lastError = error;
      if (attempt + 1 < attempts) await sleep(Math.min(250 * (2 ** attempt), 2_000));
    }
  }
  throw lastError instanceof Error ? lastError : new Error('Browser Host input request failed.');
}

export async function browserInputRequest(
  path: string,
  body: JsonRecord,
  signal: AbortSignal,
): Promise<unknown> {
  const token = localStorage.getItem('boclaw_token');
  const response = await fetch(`/api${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
    signal,
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({})) as { code?: unknown; message?: unknown };
    throw new BrowserInputRequestError(
      response.status,
      typeof payload.code === 'string' ? payload.code : 'input_request_failed',
      typeof payload.message === 'string' ? payload.message : undefined,
    );
  }
  return response.json();
}

export class BrowserInputRequestError extends Error {
  constructor(readonly status: number, readonly code: string, readonly userMessage?: string) {
    super(userMessage || 'Browser Host input request failed.');
  }
}

/** A definitive content rejection (e.g. compression bomb): never fall back to another upload path. */
export function isHostInputRejection(error: unknown): error is BrowserInputRequestError {
  return error instanceof BrowserInputRequestError && error.code === 'input_archive_rejected';
}

function isRetryable(error: unknown): boolean {
  return !(error instanceof BrowserInputRequestError) ||
    error.status >= 500 || error.status === 408 || error.status === 429 ||
    error.code === 'input_offset_conflict';
}

function validateUploadOptions(options: BrowserHostInputUploadOptions): void {
  if (!options || typeof options !== 'object' || !(options.file instanceof File)) {
    throw new Error('Browser Host input upload options are invalid.');
  }
  validateId('sessionId', options.sessionId);
  validateId('turnId', options.turnId);
  if (options.file.size < 1 || options.file.size > BROWSER_HOST_INPUT_MAX_BYTES ||
    !options.file.name || new TextEncoder().encode(options.file.name).byteLength > 255 ||
    /[\\/\0]/.test(options.file.name) ||
    options.file.name === '.' || options.file.name === '..' ||
    !validMediaType(options.file.type)) {
    throw new Error('Browser Host input file is invalid.');
  }
}

function validMediaType(value: unknown): value is string {
  return typeof value === 'string' &&
    /^[a-z0-9][a-z0-9.+-]*\/[a-z0-9][a-z0-9.+-]*$/.test(value);
}

function validateId(name: string, value: unknown): asserts value is string {
  if (typeof value !== 'string' || !/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/.test(value)) {
    throw new Error(`Browser Host input ${name} is invalid.`);
  }
}

function exactRecord(value: unknown, keys: readonly string[]): JsonRecord {
  if (!value || typeof value !== 'object' || Array.isArray(value) ||
    Object.getPrototypeOf(value) !== Object.prototype || Object.keys(value).length !== keys.length ||
    !keys.every(key => Object.prototype.hasOwnProperty.call(value, key))) {
    throw new Error('Browser Host input response is invalid.');
  }
  return value as JsonRecord;
}

function validRef(value: unknown, kind: 'input'): value is `input:v1:${string}`;
function validRef(value: unknown, kind: 'upload'): value is `upload:v1:${string}`;
function validRef(value: unknown, kind: 'input' | 'upload'): boolean {
  return typeof value === 'string' &&
    new RegExp(`^${kind}:v1:[A-Za-z0-9][A-Za-z0-9._~-]{0,479}$`).test(value);
}

function validChecksum(value: unknown): value is { algorithm: 'sha256'; digest: string } {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const checksum = value as JsonRecord;
  return Object.keys(checksum).length === 2 && checksum.algorithm === 'sha256' &&
    typeof checksum.digest === 'string' && /^[a-f0-9]{64}$/.test(checksum.digest);
}

function base64(value: Uint8Array): string {
  let binary = '';
  for (let offset = 0; offset < value.length; offset += 0x8000) {
    binary += String.fromCharCode(...value.subarray(offset, offset + 0x8000));
  }
  return btoa(binary);
}
